import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { timeAgo } from '@/lib/utils';
import { Project, statusLabels } from '@/types/project';
import DashboardNavbar from '@/components/feature/DashboardNavbar';
import EditProjectModal from '@/components/feature/EditProjectModal';


export default function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'archived'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);

  // Fetch projects from Supabase
  useEffect(() => {
    async function fetchProjects() {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('projects')
        .select('*')
        .order('updated_at', { ascending: false });

      if (fetchError) {
        setError('Projeler yüklenirken bir hata oluştu');
        setLoading(false);
        return;
      }

      setProjects(data || []);
      setLoading(false);
    }

    fetchProjects();
  }, []);

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter((p) => p.status === filter);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectTitle.trim()) return;

    setCreating(true);
    setCreateError('');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setCreateError('Kullanıcı girişi gerekli');
      setCreating(false);
      return;
    }

    const { data, error: insertError } = await supabase
      .from('projects')
      .insert({
        user_id: user.id,
        title: newProjectTitle.trim(),
        description: newProjectDesc.trim() || null,
        status: 'active',
      })
      .select()
      .single();

    if (insertError) {
      setCreateError('Proje oluşturulurken bir hata oluştu');
      setCreating(false);
      return;
    }

    if (data) {
      setProjects([data, ...projects]);
    }

    setNewProjectTitle('');
    setNewProjectDesc('');
    setCreateError('');
    setShowCreateModal(false);
    setCreating(false);
  };

  const openEditModal = (e: React.MouseEvent, project: Project) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectToEdit(project);
    setShowEditModal(true);
  };

  const handleSaveProject = (updated: Project) => {
    setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  const openDeleteModal = (e: React.MouseEvent, project: Project) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    setError('');

    const { error: deleteError } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectToDelete.id);

    if (deleteError) {
      setError('Proje silinirken bir hata oluştu');
      setDeleting(false);
      return;
    }

    setProjects(projects.filter((p) => p.id !== projectToDelete.id));
    setShowDeleteModal(false);
    setProjectToDelete(null);
    setDeleting(false);
  };

  const activeCount = projects.filter((p) => p.status === 'active').length;
  const completedCount = projects.filter((p) => p.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <DashboardNavbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <DashboardNavbar />

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">Projelerim</h1>
            <p className="text-sm text-stone-500 mt-1">
              {activeCount} aktif, {completedCount} tamamlanmış proje
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Yeni Proje
          </button>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
          {(['all', 'active', 'completed', 'archived'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === f
                  ? 'bg-stone-800 text-white'
                  : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-100'
              }`}
            >
              {f === 'all' && 'Tümü'}
              {f === 'active' && 'Aktif'}
              {f === 'completed' && 'Tamamlandı'}
              {f === 'archived' && 'Arşivlendi'}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
              <i className="ri-folder-open-line text-stone-400 text-2xl"></i>
            </div>
            <p className="text-stone-500 text-sm">Henüz proje bulunmuyor</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredProjects.map((project) => {
              const status = statusLabels[project.status];
              return (
                <Link
                  key={project.id}
                  to={`/project/${project.id}`}
                  className="group bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-md transition-all cursor-pointer relative"
                >
                  {/* Action buttons */}
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => openEditModal(e, project)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-stone-400 hover:text-amber-600 hover:bg-white transition-all cursor-pointer"
                      title="Projeyi düzenle"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={(e) => openDeleteModal(e, project)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-stone-400 hover:text-red-500 hover:bg-white transition-all cursor-pointer"
                      title="Projeyi sil"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>

                  <div className="w-full h-44 overflow-hidden bg-stone-100">
                    {project.thumbnail_url ? (
                      <img
                        src={project.thumbnail_url}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-folder-3-line text-stone-300 text-4xl"></i>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                      <span className="text-xs text-stone-400 flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {timeAgo(project.updated_at)}
                      </span>
                    </div>
                    <h3 className="font-semibold text-stone-800 text-sm mb-1 line-clamp-1">
                      {project.title}
                    </h3>
                    {project.description && (
                      <p className="text-stone-500 text-sm line-clamp-2">
                        {project.description}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-stone-800">Yeni Proje Oluştur</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError('');
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-stone-500"></i>
              </button>
            </div>

            {createError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Proje Adı
                </label>
                <input
                  type="text"
                  value={newProjectTitle}
                  onChange={(e) => setNewProjectTitle(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  placeholder="Örn: Web Sitesi Yenileme"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={newProjectDesc}
                  onChange={(e) => setNewProjectDesc(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none"
                  placeholder="Proje hakkında kısa bir açıklama..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-2.5 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {creating ? 'Oluşturuluyor...' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && projectToEdit && (
        <EditProjectModal
          project={projectToEdit}
          onSave={handleSaveProject}
          onClose={() => {
            setShowEditModal(false);
            setProjectToEdit(null);
          }}
        />
      )}


      {/* Delete Project Modal */}
      {showDeleteModal && projectToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50">
                <i className="ri-delete-bin-line text-red-500"></i>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-stone-800">Projeyi Sil</h2>
                <p className="text-sm text-stone-500">
                  "{projectToDelete.title}" projesini silmek istediğinize emin misiniz?
                </p>
              </div>
            </div>

            <p className="text-xs text-stone-400 mb-6">
              Bu işlem geri alınamaz. Proje ve içindeki tüm veriler (listeler, görevler, dosyalar, resimler, çizimler) kalıcı olarak silinecektir.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setProjectToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 py-2.5 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteProject}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
